import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import Langs from "../../app/lang/langs";

/**
 * Wraps a single-line text input and shows the tail of a suggested value as
 * greyed-out ghost text right after what the user typed, with a "press Tab"
 * hint underneath. Tab accepts the suggestion, Escape dismisses it, and typing
 * anything else just carries on — the suggestion is never applied on its own.
 *
 * Framework-agnostic on purpose: the child can be a PrimeReact InputText, a MUI
 * TextField or a plain <input>. The ghost text is an overlay aligned on the real
 * input's own box and font, with the typed part rendered transparent so the
 * input's actual text shows through it.
 *
 * @param {string} value       the input's current value (controlled by the parent)
 * @param {string} completion  the full suggested value, or null/'' when there is none
 * @param {function} onAccept  called with the completion when the user accepts it
 * @param {string} hint        translation key for the hint (touch devices have no
 *                             Tab key — pass TAP_TO_AUTOCOMPLETE there)
 * @param {object} sx          styles for the wrapper
 * @param {node} children      the input to decorate — must render a single <input>
 */
export function InlineCompletionField({
  value,
  completion,
  onAccept,
  hint = "TAB_TO_AUTOCOMPLETE",
  sx,
  children,
}) {
  const wrapperRef = useRef(null);
  const [metrics, setMetrics] = useState(null);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const typed = value == null ? "" : String(value);
  // only suggest while the completion actually extends what was typed
  const suggestion =
    completion && completion !== typed && completion.startsWith(typed) ? completion : "";
  const suffix = suggestion ? suggestion.slice(typed.length) : "";
  const active = Boolean(suffix) && focused && !dismissed;

  // a fresh keystroke revives a dismissed suggestion
  useEffect(() => setDismissed(false), [typed]);

  const measure = useCallback(() => {
    const wrapper = wrapperRef.current;
    const input = wrapper && wrapper.querySelector("input");
    if (!wrapper || !input) return;

    const wrapperBox = wrapper.getBoundingClientRect();
    const inputBox = input.getBoundingClientRect();
    const style = window.getComputedStyle(input);

    setMetrics({
      left: inputBox.left - wrapperBox.left,
      top: inputBox.top - wrapperBox.top,
      width: inputBox.width,
      height: inputBox.height,
      // the overlay covers the input's border box, so borders count as padding
      paddingLeft: `calc(${style.paddingLeft} + ${style.borderLeftWidth})`,
      paddingRight: `calc(${style.paddingRight} + ${style.borderRightWidth})`,
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      fontWeight: style.fontWeight,
      fontStyle: style.fontStyle,
      letterSpacing: style.letterSpacing,
      textIndent: style.textIndent,
      scrollLeft: input.scrollLeft,
    });
  }, []);

  useEffect(() => {
    if (!active) return
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [active, typed, measure]);

  // native listeners so the child's own onKeyDown/onFocus props stay untouched
  useEffect(() => {
    const input = wrapperRef.current && wrapperRef.current.querySelector("input");
    if (!input) return;

    const handleKeyDown = (event) => {
      if (!suffix || dismissed) return;
      if (event.key === "Tab" && !event.shiftKey) {
        event.preventDefault();
        onAccept(suggestion);
      } else if (event.key === "Escape") {
        setDismissed(true);
      }
    };
    const handleFocus = () => setFocused(true);
    const handleBlur = () => setFocused(false);

    input.addEventListener("keydown", handleKeyDown);
    input.addEventListener("focus", handleFocus);
    input.addEventListener("blur", handleBlur);
    input.addEventListener("scroll", measure);
    if (document.activeElement === input) setFocused(true);

    return () => {
      input.removeEventListener("keydown", handleKeyDown);
      input.removeEventListener("focus", handleFocus);
      input.removeEventListener("blur", handleBlur);
      input.removeEventListener("scroll", measure);
    };
  }, [suffix, suggestion, dismissed, onAccept, measure]);

  return (
    <Box ref={wrapperRef} sx={{ position: "relative", width: "100%", ...sx }}>
      {children}
      {active && metrics && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            pointerEvents: "none",
            overflow: "hidden",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            background: "none",
          }}
          style={{
            left: metrics.left,
            top: metrics.top,
            width: metrics.width,
            height: metrics.height,
            paddingLeft: metrics.paddingLeft,
            paddingRight: metrics.paddingRight,
            fontFamily: metrics.fontFamily,
            fontSize: metrics.fontSize,
            fontWeight: metrics.fontWeight,
            fontStyle: metrics.fontStyle,
            letterSpacing: metrics.letterSpacing,
            textIndent: metrics.textIndent,
          }}
        >
          <Box
            component="span"
            style={{ transform: `translateX(${-metrics.scrollLeft}px)` }}
            sx={{ whiteSpace: "pre", display: "inline-flex" }}
          >
            <span style={{ color: "transparent" }}>{typed}</span>
            <Box component="span" sx={{ color: "text.disabled" }}>{suffix}</Box>
          </Box>
        </Box>
      )}
      {active && (
        <Typography
          variant="caption"
          // mousedown would blur the input and unmount the hint before the click
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onAccept(suggestion)}
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            mt: "2px",
            color: "text.secondary",
            whiteSpace: "nowrap",
            cursor: "pointer",
            zIndex: 2,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <Langs str={hint} />
        </Typography>
      )}
    </Box>
  );
}
